import { useState, useRef } from 'react';
import { UploadCloud, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { galeriaApi } from '../../lib/api';
import logger from '../../lib/logger';

const BATCH = 4;
const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];

export const PhotoUploader = ({ albumId, onUploaded }) => {
  const [queue, setQueue] = useState([]);
  const [running, setRunning] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (files) => {
    const items = Array.from(files).map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`, file, preview: URL.createObjectURL(file),
      status: ACCEPT.includes(file.type) ? (file.size > 12 * 1024 * 1024 ? 'erro' : 'pendente') : 'erro',
      erro: !ACCEPT.includes(file.type) ? 'Formato não permitido' : file.size > 12 * 1024 * 1024 ? 'Acima de 12 MB' : null, progresso: 0,
    }));
    setQueue((q) => [...q, ...items]);
  };

  const update = (ids, patch) => setQueue((q) => q.map((it) => (ids.includes(it.id) ? { ...it, ...(typeof patch === 'function' ? patch(it) : patch) } : it)));

  const start = async () => {
    setRunning(true);
    const pend = queue.filter((i) => i.status === 'pendente');
    for (let i = 0; i < pend.length; i += BATCH) {
      const lote = pend.slice(i, i + BATCH);
      const ids = lote.map((l) => l.id);
      const fd = new FormData();
      lote.forEach((l) => fd.append('files', l.file));
      update(ids, { status: 'enviando' });
      try {
        const res = await galeriaApi.upload(albumId, fd, (e) => update(ids, { progresso: e.total ? Math.round((e.loaded / e.total) * 100) : 50 }));
        const errosPorNome = Object.fromEntries((res.data.erros || []).map((er) => [er.arquivo, er.erro]));
        update(ids, (it) => (errosPorNome[it.file.name] ? { status: 'erro', erro: errosPorNome[it.file.name] } : { status: 'ok', progresso: 100 }));
        onUploaded?.(res.data.fotos);
      } catch (err) {
        logger.error(err);
        const msg = err.response?.data?.detail || 'Falha no envio';
        update(ids, { status: 'erro', erro: typeof msg === 'string' ? msg : 'Falha no envio' });
      }
    }
    setRunning(false);
  };

  const pendentes = queue.filter((i) => i.status === 'pendente').length;
  const total = queue.length;
  const feitos = queue.filter((i) => i.status === 'ok' || i.status === 'erro').length;

  return (
    <div className="space-y-4" data-testid="photo-uploader">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${drag ? 'border-kairos-orange bg-kairos-orange/10' : 'border-white/15 hover:border-kairos-orange/50 bg-obsidian-50'}`} data-testid="dropzone">
        <UploadCloud className="w-10 h-10 text-kairos-orange mx-auto mb-3" />
        <p className="text-white font-medium">Arraste as fotos aqui ou clique para selecionar</p>
        <p className="text-gray-500 text-sm mt-1">JPG, PNG ou WebP • até 12 MB cada • envio em lotes de {BATCH}, pode selecionar centenas</p>
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="file-input" />
      </div>

      {total > 0 && (
        <>
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="flex justify-between text-xs text-gray-400 mb-1"><span>{feitos}/{total} processadas</span><span>{queue.filter((i) => i.status === 'erro').length} com erro</span></div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-kairos-orange transition-all" style={{ width: `${total ? (feitos / total) * 100 : 0}%` }} data-testid="upload-progress" /></div>
            </div>
            <Button onClick={start} disabled={running || pendentes === 0} className="bg-kairos-orange hover:bg-kairos-orange-dark text-white" data-testid="start-upload-btn">
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</> : `Publicar ${pendentes} foto(s)`}
            </Button>
            <Button variant="ghost" onClick={() => setQueue((q) => q.filter((i) => i.status !== 'ok'))} className="text-gray-400" data-testid="clear-done-btn">Limpar concluídas</Button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-10 gap-2" data-testid="upload-queue">
            {queue.map((it) => (
              <div key={it.id} className="relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-black" title={it.erro || it.file.name}>
                <img src={it.preview} alt="" className={`w-full h-full object-cover ${it.status === 'ok' ? 'opacity-60' : ''}`} />
                {it.status === 'enviando' && <div className="absolute inset-x-0 bottom-0 h-1 bg-kairos-orange" style={{ width: `${it.progresso}%` }} />}
                {it.status === 'ok' && <CheckCircle className="absolute top-1 right-1 w-4 h-4 text-green-400" />}
                {it.status === 'erro' && <AlertCircle className="absolute top-1 right-1 w-4 h-4 text-red-400" />}
                {it.status === 'pendente' && <button onClick={(e) => { e.stopPropagation(); setQueue((q) => q.filter((x) => x.id !== it.id)); }} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white text-xs" aria-label="Remover da fila">×</button>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
